import React, { Fragment } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Edit as EditIcon, Delete as DeleteIcon, Info as InfoIcon } from "@material-ui/icons";
import Paper from "@material-ui/core/Paper";
import { Box } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import PropTypes from "prop-types";
import Collapse from "@material-ui/core/Collapse";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { Pagination } from "../Pagination";

const styles = makeStyles({});

const DataTable = ({
  fields,
  onDelete = () => true,
  onEdit = () => true,
  onView = () => true,
  data = [],
  fieldNames = {},
  fieldFormats = {},
  deleteButton = true,
  editButton = true,
  viewButton = false,
  extraButtons = [],
  groupedData = false,
  pagination,
}) => {
  const classes = makeStyles({});
  const fields_ = fields || Object.keys(data[0] || {});

  // Set open/close panel for grouped table
  const [toggleState, setToggleState] = React.useState(
    Array(data.length).fill(false)
  );
  const toggleOpen = (idx) => {
    const state = toggleState.map((x, row) => (idx === row ? !x : false));
    setToggleState(state);
  };

  // Generate custom key for each row
  const generateIdxKey = (idx, row) => {
    return `row-${String(idx)}-${String(row[Object.keys(row)[0]])}`;
  };

  // Render simple header
  const generateHeader = () => {
    return (
      <TableRow>
        {fields_.map((field, idx) => (
          <TableCell key={field.toString() + idx.toString()}>
            {fieldNames[field] || field}
          </TableCell>
        ))}
        {editButton && <TableCell style={{ width: "100px" }} />}
        {viewButton && <TableCell style={{ width: "100px" }} />}
        {deleteButton && <TableCell style={{ width: "100px" }} />}
      </TableRow>
    );
  };

  // Render grouped header
  const generateGroupedHeader = () => {
    return <TableRow />;
  };

  // Main function to render header
  const renderHeader = () => {
    if (!data) {
      return undefined;
    }
    return !groupedData ? generateHeader() : generateGroupedHeader();
  };

  // Generate final markup row
  const generateRows = (rows) => {
    return rows.map((row, idx) => (
      <TableRow key={generateIdxKey(idx, row)} data-testid="data-table-row">
        {fields_.map((field) => (
          <TableCell
            key={row[field] + field.toString()}
            component="th"
            scope="row"
          >
            {fieldFormats[field] ? fieldFormats[field](row[field]) : row[field]}
          </TableCell>
        ))}
        {editButton && (
          <TableCell component="th" align="right">
            <IconButton
              variant="contained"
              color="default"
              onClick={() => onEdit(row)}
              className={styles.button}
              data-testid="data-table-edit-button"
            >
              <EditIcon data-testid="edit-icon" />
            </IconButton>
          </TableCell>
        )}
        {viewButton && (
            <TableCell component="th" align="right">
              <IconButton
                  variant="contained"
                  color="default"
                  onClick={() => onView(row)}
                  className={styles.button}
                  data-testid="data-table-view-button"
              >
                <InfoIcon data-testid="view-icon" />
              </IconButton>
            </TableCell>
        )}
        {deleteButton && (
          <TableCell component="th" align="right">
            <IconButton
              variant="contained"
              color="secondary"
              onClick={() => onDelete(row)}
              className={styles.button}
              data-testid="data-table-delete-button"
            >
              <DeleteIcon data-testid="delete-icon" />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    ));
  };

  // If rows are grouped make additional loop
  const generateGroupedRows = () => {
    return data.map((row, idx) => (
      <Fragment key={`fragment-${idx}`}>
        <TableRow
          key={`wrapper-${row.groupName}-${idx}`}
          data-testid="data-table-row"
        >
          <TableCell style={{ width: "10%" }}>
            <IconButton size="small" onClick={() => toggleOpen(idx)}>
              {toggleState[idx] ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </TableCell>
          <TableCell style={{ width: "90%" }}>{row.groupName}</TableCell>
        </TableRow>
        <TableRow key={`child-${row.groupName}-${idx}`}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
            <Collapse in={toggleState[idx]} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Table size="small">
                  <TableHead>{generateHeader()}</TableHead>
                  <TableBody>{generateRows(row.rows)}</TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </Fragment>
    ));
  };

  // Main function to render each row
  const renderRows = () => {
    if (!data) {
      return undefined;
    }
    return !groupedData ? generateRows(data) : generateGroupedRows();
  };

  // If pagination is set, render component
  let paginationComp = undefined;
  if (pagination !== undefined) {
    paginationComp = (
      <TableFooter>
        <TableRow>
          <Pagination options={pagination}></Pagination>
        </TableRow>
      </TableFooter>
    );
  }

  return (
    <TableContainer component={Paper} data-testid="data-table-wrapper">
      <Table className={classes.table} data-testid="data-table">
        <TableHead data-testid="data-table-header">{renderHeader()}</TableHead>
        <TableBody data-testid="data-table-body">{renderRows()}</TableBody>
        {paginationComp}
      </Table>
    </TableContainer>
  );
};

export default DataTable;

DataTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  fields: PropTypes.arrayOf(PropTypes.string),
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onView: PropTypes.func,
  deleteButton: PropTypes.bool,
  editButton: PropTypes.bool,
  viewButton: PropTypes.bool,
  fieldNames: PropTypes.object,
  fieldFormats: PropTypes.object,
  extraButtons: PropTypes.arrayOf(PropTypes.object),
  groupedData: PropTypes.bool,
  pagination: PropTypes.object,
};
